import React from 'react';
import {Col, DatePicker, Form, Input, Row, Typography} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';

import {dateFormat} from "../../lib/time";
import {notMatchingPassword} from "../../helpers/text";
import PropTypes from "prop-types";

const {Text} = Typography;

/**
 * This component is used to fill personal information when creating an account.
 * Or when adding a non-existent person to a pre-existing account.
 * Involves a person's name, birthdate, email, and password.
 *
 * It is imported into two places:
 * - AccountPersonAdd - passwordRequired = false
 * - AccountCreateInformation - passwordRequired = true
 *
 * The input fields are conditionally required depending on where the component is being imported
 * (passwordRequired param indicates this) and if it's a business account.
 * Password for AccountPersonAdd is optional, a user record is stored for having an associated email,
 * but password is not hashed and stored as plain text (notapassword).
 *
 */
const PersonalInformationForm = ({
                                     isBusiness,
                                     firstName,
                                     setFirstName,
                                     lastName,
                                     setLastName,
                                     birthDate,
                                     setBirthDate,
                                     email,
                                     setEmail,
                                     passwordRequired,
                                     password,
                                     setPassword,
                                     confirmPassword,
                                     setConfirmPassword,
                                 }) => {

    const handleFirstNameChange = (e) => {
        setFirstName(e.target.value.trim());
    }

    const handleLastNameChange = (e) => {
        setLastName(e.target.value.trim());
    }

    const handleBirthDateChange = (date, dateString) => {
        setBirthDate(dayjs(dateString).tz());
    }

    const handleEmailChange = (e) => {
        setEmail(e.target.value.trim());
    }

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
    }

    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
    }

    // There is some messy UX sequences that can occur such as lingering error messages as the ConfirmPassword
    // Form.Item is dynamically required. It's split up into two to handle this, PasswordRequired is a param that
    // determines if AccountAddPerson or AccountCreateInformation is importing this component, and therefore which to use.
    const confirmPasswordRequiredJSX =
        <Form.Item
            name="Confirm Password"
            label="Confirm Password"
            dependencies={['Password']}
            hasFeedback
            rules={[{
                required: true,
                message: 'Please confirm your password!',
            },
                ({getFieldValue}) => ({
                    validator(_, value) {
                        if (!value || getFieldValue('Password') === value) {
                            return Promise.resolve();
                        }
                        return Promise.reject(new Error(notMatchingPassword));
                    },
                }),
            ]}
            initialValue={confirmPassword}
        >
            <Input.Password name="confirmPassword" onChange={handleConfirmPasswordChange}
                            visibilityToggle={false}/>
        </Form.Item>

    const confirmPasswordNotRequiredJSX =
        <Form.Item
            name="Confirm Password"
            label="Confirm Password"
            dependencies={['Password']}
            rules={[{
                required: password,
                message: 'Please confirm your password!',
            },
                ({getFieldValue}) => ({
                    validator(_, value) {
                        if (!value || getFieldValue('Password') === value) {
                            return Promise.resolve();
                        }
                        return Promise.reject(new Error(notMatchingPassword));
                    },
                }),
            ]}
            hasFeedback
            initialValue={confirmPassword}
            validateStatus={password ? ((password === confirmPassword) ? 'success' : 'error') : ''}
            help={password ? ((password === confirmPassword) ? '' : notMatchingPassword) : ''}
        >
            <Input.Password name="confirmPassword" onChange={handleConfirmPasswordChange}
                            visibilityToggle={false}/>
        </Form.Item>

    return (
        <Row gutter={16} className="personal-information-form">
            <Col xs={24} sm={24} md={12}>
                <Form.Item>
                    <Text strong className="account-create-information-sub-title">
                        {isBusiness ? "Account Owner Information" : "Profile Information"}
                    </Text>
                </Form.Item>
                <Form.Item
                    name="First Name"
                    label="First Name"
                    rules={[{type: 'string', required: true, whitespace: true, message: "'First Name' is required"}]}
                    initialValue={firstName}
                >
                    <Input name="firstName" onChange={handleFirstNameChange}/>
                </Form.Item>
                <Form.Item
                    name="Last Name"
                    label="Last Name"
                    rules={[{type: 'string', required: true, whitespace: true, message: "'Last Name' is required"}]}
                    initialValue={lastName}
                >
                    <Input name="lastName" onChange={handleLastNameChange}/>
                </Form.Item>
                <Form.Item
                    name="Birth Date"
                    label="Birth Date"
                    rules={!isBusiness && [{type: 'object', required: true, message: "'Birth Date' is required"}]}
                    initialValue={birthDate}
                >
                    <DatePicker
                        format={dateFormat}
                        style={{width: '100%'}}
                        onChange={handleBirthDateChange}
                        allowClear={false}
                    />
                </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12}>
                <Form.Item>
                    <Text strong className="account-create-information-sub-title">Login Information</Text>
                </Form.Item>
                <Form.Item
                    name="Email"
                    label="Email"
                    rules={[{
                        type: 'email',
                        required: true,
                        message: "The input is not a valid email"
                    }]}
                    initialValue={email}
                >
                    <Input name="email" onChange={handleEmailChange}/>
                </Form.Item>
                <Form.Item
                    name="Password"
                    label="Password"
                    rules={passwordRequired ? [{required: true, min: 8}] : [{min: password ? 8 : 0}]}
                    tooltip={
                        {
                            title: !passwordRequired ?
                                'Password is optional. If the person desires login privileges please fill. Must be at least 8 characters long.'
                                : 'Must be at least 8 characters long.',
                            icon: <InfoCircleOutlined/>
                        }
                    }
                    initialValue={password}
                >
                    <Input.Password name="password" onChange={handlePasswordChange} visibilityToggle={false}/>
                </Form.Item>
                {passwordRequired ? confirmPasswordRequiredJSX : confirmPasswordNotRequiredJSX}
            </Col>
        </Row>
    )
}

export default PersonalInformationForm;

PersonalInformationForm.propTypes = {
    isBusiness: PropTypes.bool.isRequired,
    firstName: PropTypes.string,
    setFirstName: PropTypes.func.isRequired,
    lastName: PropTypes.string,
    setLastName: PropTypes.func.isRequired,
    birthDate: PropTypes.object,
    setBirthDate: PropTypes.func.isRequired,
    email: PropTypes.string,
    setEmail: PropTypes.func.isRequired,
    passwordRequired: PropTypes.bool.isRequired,
    password: PropTypes.string,
    setPassword: PropTypes.func.isRequired,
    confirmPassword: PropTypes.string,
    setConfirmPassword: PropTypes.func.isRequired
}
